import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { InvoiceEntity } from './invoice.entity';
import { PaymentScheduleOccurrenceEntity } from './payment-schedule-occurrence.entity';

/**
 * PD-15708 / PD-15852 — one planned line of a cycle occurrence.
 *
 * A cycle occurrence consolidates many invoices into one charge. This table
 * is the reviewable plan (what the run intends to apply to each document,
 * snapshotted at planning time) and, after release, the record of what was
 * actually applied. `plannedAmount` follows the allocation convention the
 * capture path uses: positive for an invoice, negative for a credit memo,
 * fee-inclusive where the document already carries a fee.
 */
export type PaymentScheduleOccurrenceAllocationKind = 'invoice' | 'credit_memo';

@Index(
	'payment_schedule_occurrence_allocation_occurrence_invoice_unique',
	['fkPaymentScheduleOccurrenceId', 'fkInvoiceId'],
	{ unique: true },
)
@Index(
	'payment_schedule_occurrence_allocation_fk_invoice__id',
	['fkInvoiceId'],
	{},
)
@Entity(
	'payment_schedule_occurrence_allocation',
	{},
	{ label: 'Payment Schedule Occurrence Allocation' },
)
export class PaymentScheduleOccurrenceAllocationEntity extends BaseEntity {
	@Column('int', {
		name: '_fk_payment_schedule_occurrence__id',
		unsigned: true,
		comment: 'The cycle occurrence this line belongs to',
	})
	public fkPaymentScheduleOccurrenceId!: number;

	@Column('int', {
		name: '_fk_invoice__id',
		unsigned: true,
		comment: 'The invoice or credit memo this line applies to',
	})
	public fkInvoiceId!: number;

	@Column('varchar', {
		name: 'kind',
		length: 16,
		default: () => "'invoice'",
		comment: "'invoice' (positive) or 'credit_memo' (negative)",
	})
	public kind!: PaymentScheduleOccurrenceAllocationKind;

	@Column('decimal', {
		name: 'plannedAmount',
		precision: 13,
		scale: 2,
		comment: 'What the run planned to apply, snapshotted at planning time',
	})
	public plannedAmount!: string;

	@Column('decimal', {
		name: 'appliedAmount',
		nullable: true,
		precision: 13,
		scale: 2,
		comment:
			'What actually landed on the document after release (gross, fee included); NULL until released',
	})
	public appliedAmount!: string | null;

	@Column('varchar', {
		name: 'skipReason',
		nullable: true,
		length: 64,
		comment:
			'Why the line was dropped at release (nothing_owed, held_on_order, cancelled …)',
	})
	public skipReason!: string | null;

	// --- Relationships ---

	@ManyToOne(
		() => PaymentScheduleOccurrenceEntity,
		(occurrence) => occurrence.allocations,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{ name: '_fk_payment_schedule_occurrence__id', referencedColumnName: 'id' },
	])
	public fkPaymentScheduleOccurrence!: PaymentScheduleOccurrenceEntity;

	@ManyToOne(() => InvoiceEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity;
}
